export * from './format';
import { isNil, merge, isArray } from 'lodash';
import numeral from 'numeral';
import queryString from 'query-string';

/**
 * 判断是否超出发布时间${day}天
 * @param {number} releaseDate 发布时间
 * @param {number} days 是否超出${day}天
 * @return {boolean}
 */
export const checkTime = (releaseDate: number, days: number): boolean => {
    const date = new Date(releaseDate);
    const endDate = new Date(date);
    endDate.setDate(date.getDate() + days);

    return endDate.getTime() > Date.now();
};

export function flatOption(option, childrenKey: string = 'children') {
    return isArray(option[childrenKey])
        ? [option, ...option[childrenKey].reduce((opts, opt) => opts.concat(flatOption(opt)), [])]
        : [option];
}

export function flatTree(option) {
    let opts = [];
    if (option) {
        opts.push(option);
    }
    if (option?.children) {
        opts = opts.concat(
            option.children.reduce((pre, cur) => {
                return pre.concat(flatTree(cur));
            }, []),
        );
    }
    return opts;
}

export function stripOptionsPrefix(options, keys: string | string[] = 'label', spliter = '-') {
    if (!Array.isArray(keys)) keys = [keys];

    return options?.map((o) => {
        const option = {};
        keys.forEach((key) => {
            if (typeof o[key] !== 'string') return;
            const arr = o[key].split(spliter);
            option[key] = arr[arr.length - 1].trim();
        });
        return option;
    });
}

export function fillLabel(tree, prefix) {
    if (prefix) {
        tree.label = prefix + tree.label;
    }
    tree.value = tree.label;

    if (tree.children) {
        tree.children.forEach((subtree) => fillLabel(subtree, tree.label + '-'));
    }
}
export function fillTreeValue(tree, prefix) {
    tree.value = prefix + tree.label;

    if (tree.children) {
        tree.children.forEach((subtree) => fillTreeValue(subtree, tree.value + '-'));
    }
}
export function updateTreeValue(tree) {
    tree.value = tree.value.replace(/-/g, '_');

    if (tree.children) {
        tree.children.forEach((subtree) => updateTreeValue(subtree));
    }
}
// 电商行为-增加hierarchyLabel属性
export function goodsAddTreeHierarchyLabel(tree, prefix) {
    tree.hierarchyLabel = tree.value.replace(prefix, '');
    tree.rawValue = tree.value;
    tree.rawLabel = tree.label;

    if (tree.children) {
        if (tree.children.length === 0) {
            tree.children = null;
        } else {
            tree.children.forEach((subtree) => goodsAddTreeHierarchyLabel(subtree, prefix));
        }
    }
}
export function goodsFillTreeHierarchyLabel(tree, preifx) {
    tree.hierarchyLabel = preifx + tree.label;

    if (tree.children) {
        tree.children.forEach((subtree) =>
            goodsFillTreeHierarchyLabel(subtree, tree.hierarchyLabel + '-'),
        );
    }
}
// 电商行为增加类别
export function fillTreeCategory(tree, category) {
    tree.category = category;

    if (tree.children) {
        tree.children.forEach((subtree) => fillTreeCategory(subtree, category));
    }
}

export function convertOptions(options, pName) {
    return (
        options?.map((item) =>
            item.children?.length
                ? {
                      label: item.label,
                      value: pName ? `${pName}-${item.label}` : item.label,
                      cover: item.cover,
                      des: item.des,
                      coverText: item.coverText,
                      children: convertOptions(
                          item.children,
                          pName ? `${pName}-${item.label}` : item.label,
                      ),
                  }
                : {
                      label: item.label,
                      value: pName ? `${pName}-${item.label}` : item.label,
                      children: [],
                      des: item.des,
                      cover: item.cover,
                      coverText: item.coverText,
                  },
        ) || []
    );
}

// 格式化百分比
export function statusDetailFormatPercent(
    num: number | string | undefined | null,
    precision: number = 2,
    option?: { optionalPrecision: boolean },
) {
    const { optionalPrecision = false } = option ?? {};
    if (isNil(num)) return '--';

    const prefix = (Number(num) * 100).toFixed(precision);

    const points = optionalPrecision ? `${+prefix}` : prefix;

    return `${points}%`;
}

export const statusDetailFormatCover = (str: string | number | undefined | null) => {
    if (str === undefined || str === null) {
        return '-';
    }
    const num = Number(str);
    if (Number.isNaN(num)) {
        return str;
    }
    return num > 10000 ? `${Math.floor(num / 10000).toLocaleString()}万` : num.toLocaleString();
};

/**
 * 格式化整数
 * @param option.convertToW 是否格式化成 12.3w
 * @param option.precision 如果设置了convertToW：true,则必须设置小数位，否则小于 1w的会展示0w（例：9999==> 0w）
 */
export function statusDetailFormatNumber(
    num: number | string | undefined | null,
    options?: Partial<{
        precision: number;
        optionalPrecision: boolean;
        convertToW: boolean;
        defaultVal: string;
    }>,
) {
    if (isNil(num)) return options?.defaultVal ?? '--';
    const _num = Number(num);
    if (isNaN(_num)) {
        return num as string;
    }
    const mergedOptions = merge({ precision: 0, convertToW: false }, options);
    const { precision, convertToW, optionalPrecision } = mergedOptions;
    let dotsStr = '';
    for (let i = 0; i < precision; i++) {
        if (optionalPrecision) {
            dotsStr += '[0]';
        } else {
            dotsStr += '0';
        }
    }
    const formatStr = `0,0.${dotsStr}`;
    if (convertToW) {
        return `${numeral(_num / 10000).format(formatStr)}w`;
    }
    return numeral(_num).format(formatStr);
}

export function isBase64(str: string) {
    return /^data:image\/png/.test(str);
}

function download(url: string, name?: string) {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    if (name) {
        a.download = name;
    }
    a.click();
}

/**
 *
 * @param path 下载地址
 * @param name 下载的文件名
 * @returns
 */
export async function downloadFile(path: string, name?: string | undefined) {
    if (isBase64(path)) {
        download(path, name ?? '');
        return;
    }
    path = queryString.stringifyUrl({
        url: path,
    });

    return new Promise((resolve, reject) => {
        // 浏览器可支持
        if (!!URL?.createObjectURL && !!URL?.revokeObjectURL) {
            const xhr = new XMLHttpRequest();
            xhr.open('get', path);
            xhr.responseType = 'blob';
            xhr.send();
            xhr.onload = function () {
                if (this.status === 200 || this.status === 304) {
                    if ('msSaveOrOpenBlob' in navigator) {
                        // @ts-expect-error
                        navigator.msSaveOrOpenBlob(this.response, name);
                        return;
                    }
                    const url = URL.createObjectURL(this.response);
                    download(url, name);
                    URL.revokeObjectURL(url);
                    resolve('success');
                } else {
                    reject('downloadFile error');
                }
            };

            xhr.onerror = () => {
                reject('downloadFile error');
            };
        } else {
            download(path, name);
            resolve('success');
        }
    });
}
